import type { SeededRandom } from "../core/SeededRandom";
import type {
  EnemyKind,
  EnemySpawn,
  PickupKind,
  PickupSpawn,
  Point,
  Room,
  RoomKind,
} from "./types";
import { pointKey, TileType } from "./types";
import type { GridMap } from "./GridMap";

export const PROCEDURAL_ENEMY_FACTIONS: readonly EnemyKind[] = [
  "rustSentinel",
  "sporeCrawler",
  "guard",
  "stalker",
  "boneHound",
  "choirAcolyte",
  "forgeWarden",
] as const;

const ENEMY_PLAN: Readonly<Record<RoomKind, readonly EnemyKind[]>> = {
  start: [],
  encounter: ["guard", "boneHound"],
  hub: ["guard", "guard", "choirAcolyte", "boneHound", "guard"],
  supply: ["guard", "boneHound", "sporeCrawler"],
  secret: ["choirAcolyte"],
  tech: ["guard", "stalker", "choirAcolyte", "boneHound", "rustSentinel", "rustSentinel"],
  toxic: ["guard", "guard", "stalker", "forgeWarden", "boneHound", "sporeCrawler"],
  dark: ["stalker", "guard", "choirAcolyte", "boneHound", "sporeCrawler", "sporeCrawler", "stalker"],
  finale: ["guard", "stalker", "choirAcolyte", "boneHound", "abyssTyrant", "guard"],
};

const PICKUP_PLAN: Readonly<Partial<Record<RoomKind, readonly PickupKind[]>>> = {
  start: ["armor", "bullets"],
  encounter: ["health", "rifle"],
  hub: ["health", "armor"],
  supply: ["shotgun", "shells", "health", "armor"],
  secret: ["armor", "shells", "bullets", "rifleAmmo"],
  tech: ["armor", "bullets", "rifleAmmo"],
  toxic: ["health", "armor", "bullets"],
  dark: ["health", "armor", "shells", "rifleAmmo"],
  finale: ["health", "armor"],
};

export class SpawnPlanner {
  private readonly occupiedCells = new Set<string>();

  public constructor(
    private readonly random: SeededRandom,
    private readonly grid?: GridMap,
  ) {}

  public createEnemies(rooms: readonly Room[]): EnemySpawn[] {
    const enemies: EnemySpawn[] = [];
    for (const room of rooms) {
      const kinds = ENEMY_PLAN[room.id];
      const cells = this.roomCells(room, 2);
      for (let index = 0; index < kinds.length; index += 1) {
        const kind = kinds[index] as EnemyKind;
        const cell = cells.find((candidate) => !this.occupiedCells.has(pointKey(candidate)));
        if (!cell) throw new Error(`Нет свободной клетки для врага в зоне ${room.id}`);
        this.occupiedCells.add(pointKey(cell));
        enemies.push({
          id: `${room.id}-${kind}-${index}`,
          kind,
          roomId: room.id,
          cell,
        });
      }
    }
    return enemies;
  }

  public createPickups(rooms: readonly Room[]): PickupSpawn[] {
    const pickups: PickupSpawn[] = [];
    for (const room of rooms) {
      const kinds = PICKUP_PLAN[room.id] ?? [];
      const cells = this.roomCells(room, 1).filter(
        (candidate) => !this.occupiedCells.has(pointKey(candidate)),
      );
      for (let index = 0; index < kinds.length; index += 1) {
        const kind = kinds[index] as PickupKind;
        const cell = cells.shift();
        if (!cell) throw new Error(`Нет свободной клетки для предмета в зоне ${room.id}`);
        this.occupiedCells.add(pointKey(cell));
        pickups.push({
          id: `${room.id}-${kind}-${index}`,
          kind,
          roomId: room.id,
          cell,
        });
      }
    }
    return pickups;
  }

  private roomCells(room: Room, inset: number): Point[] {
    const cells: Point[] = [];
    for (let y = room.y + inset; y < room.y + room.height - inset; y += 1) {
      for (let x = room.x + inset; x < room.x + room.width - inset; x += 1) {
        if (
          (x !== room.center.x || y !== room.center.y) &&
          !(room.id === "start" && x === room.center.x && y === room.center.y + 1) &&
          (!this.grid || this.grid.get({ x, y }) === TileType.Floor)
        ) {
          cells.push({ x, y });
        }
      }
    }
    return this.random.shuffle(cells.length > 0 ? cells : [room.center]);
  }
}
